import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Eye, AlertTriangle, Power, ChevronRight, Lock, Server, 
  Activity, Users, Check, Globe, Database, Cpu, Cloud, Network,
  Zap, Target, BarChart3, FileText, ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('ciso');
  const { lang, setLanguage, t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#27272A]/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-[#0066FF]" />
            <span className="text-xl font-bold text-[#F9FAFB] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Agent Control Plane
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#why-now" className="nav-link">{t('nav_why_now')}</a>
            <a href="#platform" className="nav-link">{t('nav_platform')}</a>
            <a href="#deployment" className="nav-link">{t('nav_deployment')}</a>
            <a href="#security" className="nav-link">{t('nav_security')}</a>
          </div>
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <div className="flex items-center bg-[#1A1A1A] border border-[#27272A]/60" data-testid="lang-selector">
              <button
                onClick={() => setLanguage('en')}
                className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${lang === 'en' ? 'bg-[#0066FF] text-white' : 'text-[#6B7280] hover:text-[#F9FAFB]'}`}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('es')}
                className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${lang === 'es' ? 'bg-[#0066FF] text-white' : 'text-[#6B7280] hover:text-[#F9FAFB]'}`}
                data-testid="lang-es-btn"
              >
                ES
              </button>
            </div>
            <Link to="/login">
              <Button variant="outline" className="secondary-btn" data-testid="nav-login-btn">
                {t('nav_login')}
              </Button>
            </Link>
            <Link to="/request-pilot">
              <Button className="primary-btn" data-testid="nav-request-pilot-btn">
                {t('nav_request_pilot')}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(#27272A 1px, transparent 1px), linear-gradient(90deg, #27272A 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0066FF]/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#121212] border border-[#27272A] mb-8">
              <span className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" />
              <span className="text-[#9CA3AF] text-sm">{t('hero_badge')}</span>
            </div>
            
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#F9FAFB] mb-6 tracking-tight leading-[1.1]"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="hero-title"
            >
              {t('hero_title_1')} {t('hero_title_2')} {t('hero_title_3')} {t('hero_title_4')}
            </h1>
            
            <p className="text-lg text-[#9CA3AF] mb-8 max-w-xl leading-relaxed">
              {t('hero_subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10">
              <Link to="/request-pilot">
                <Button className="primary-btn text-base px-6 py-5" data-testid="hero-request-pilot-btn">
                  {t('hero_cta_pilot')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="secondary-btn text-base px-6 py-5" data-testid="hero-watch-demo-btn">
                  {t('hero_cta_explore')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-[#6B7280]">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#34C759]" />
                <span>{t('hero_trust_1')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#34C759]" />
                <span>{t('hero_trust_2')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#34C759]" />
                <span>{t('hero_trust_3')}</span>
              </div>
            </div>
          </div>
          
          {/* Dashboard Preview */}
          <div className="hidden lg:block relative">
            <div className="enterprise-card p-3">
              <div className="bg-[#0A0A0A] p-4">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#27272A]/40">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF3B30]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FFCC00]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#34C759]" />
                </div>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div className="enterprise-card p-3">
                    <div className="text-xl font-bold text-[#34C759] font-mono">82</div>
                    <div className="text-[10px] text-[#6B7280] uppercase">Posture</div>
                  </div>
                  <div className="enterprise-card p-3">
                    <div className="text-xl font-bold text-[#0066FF] font-mono">247</div>
                    <div className="text-[10px] text-[#6B7280] uppercase">Identities</div>
                  </div>
                  <div className="enterprise-card p-3">
                    <div className="text-xl font-bold text-[#FFCC00] font-mono">18</div>
                    <div className="text-[10px] text-[#6B7280] uppercase">Violations</div>
                  </div>
                  <div className="enterprise-card p-3">
                    <div className="text-xl font-bold text-[#FF3B30] font-mono">3</div>
                    <div className="text-[10px] text-[#6B7280] uppercase">Critical</div>
                  </div>
                </div>
                <div className="enterprise-card p-3 border-l-2 border-[#FF3B30]">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[#F9FAFB] font-mono text-sm">payment-processor-bot</div>
                      <div className="text-[10px] text-[#6B7280]">Unauthorized refund action</div>
                    </div>
                    <button className="px-3 py-1 bg-[#FF3B30] text-white text-xs font-bold">
                      CONTAIN
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Now Section */}
      <section id="why-now" className="py-20 bg-[#0A0A0A] border-t border-[#27272A]/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('why_now_title')}
            </h2>
            <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto">
              {t('why_now_subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="enterprise-card p-8 border-l-4 border-[#FF3B30]">
              <h3 className="text-lg font-bold text-[#F9FAFB] mb-4">{t('why_now_gap_title')}</h3>
              <ul className="space-y-3 text-[#9CA3AF] text-sm">
                {t('why_now_items').map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[#FF3B30] mt-0.5">&#x2717;</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="enterprise-card p-8 border-l-4 border-[#0066FF]">
              <h3 className="text-lg font-bold text-[#F9FAFB] mb-4">What Agent Control Plane Delivers</h3>
              <ul className="space-y-3 text-[#9CA3AF] text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-[#34C759] mt-0.5">✓</span>
                  Complete inventory of every non-human identity
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#34C759] mt-0.5">✓</span>
                  Real-time policy enforcement
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#34C759] mt-0.5">✓</span>
                  Instant kill switch for rogue agents
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#34C759] mt-0.5">✓</span>
                  Blast radius analysis before incidents
                </li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '10x', label: 'More NHIs than humans', sub: 'in enterprise environments' },
              { value: '73%', label: 'Lack visibility', sub: 'into service account usage' },
              { value: '45%', label: 'Have orphaned', sub: 'credentials in production' },
              { value: '14d', label: 'Average time', sub: 'to revoke compromised access' },
            ].map((stat, i) => (
              <div key={i} className="text-center p-5 enterprise-card">
                <div className="text-3xl font-bold text-[#0066FF] font-mono mb-1">{stat.value}</div>
                <div className="text-[#F9FAFB] text-sm font-medium">{stat.label}</div>
                <div className="text-xs text-[#6B7280]">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Modules */}
      <section id="platform" className="py-20 bg-[#121212]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Platform Modules
            </h2>
            <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto">
              {t('platform_subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                icon: Eye,
                title: 'Discovery',
                desc: 'Continuous auto-discovery across IdPs, cloud providers, and agent frameworks. Classify AI agents, service accounts, bots, tokens, and workloads.',
                color: '#0066FF'
              },
              {
                icon: Network,
                title: 'Permission Graph',
                desc: 'Visualize effective access paths. See every credential, tool, resource, and action each identity can perform. Identify excessive privileges.',
                color: '#34C759'
              },
              {
                icon: Shield,
                title: 'Policy Engine',
                desc: 'Define and enforce security policies with RBAC and ABAC. Simulate impact before deployment. Multiple enforcement modes.',
                color: '#FFCC00'
              },
              {
                icon: AlertTriangle,
                title: 'Violation Detection',
                desc: 'Real-time detection of policy violations. Missing owners, shared credentials, unapproved tools, cross-environment access.',
                color: '#FF6961'
              },
              {
                icon: Power,
                title: 'Kill Switch',
                desc: 'Instant containment: revoke tokens, disable agents, block tools, force approval mode. Isolate compromised identities in seconds.',
                color: '#FF3B30'
              },
            ].map((item, i) => (
              <div key={i} className="enterprise-card-hover p-6 flex items-start gap-5">
                <div 
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#F9FAFB] mb-1">{item.title}</h3>
                  <p className="text-[#9CA3AF] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment Modes */}
      <section id="deployment" className="py-20 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('deployment_title')}
            </h2>
            <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto">
              {t('deployment_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Cloud,
                title: 'SaaS',
                desc: 'Managed control plane with read-only connectors. Fastest deployment.',
                features: ['Managed infrastructure', 'Automatic updates', 'Read-only collection'],
                best: false
              },
              {
                icon: Server,
                title: 'Customer VPC',
                desc: 'Deploy in your cloud environment. Full data sovereignty.',
                features: ['Your infrastructure', 'Data stays in VPC', 'Customer-managed keys'],
                best: true
              },
              {
                icon: Lock,
                title: 'Self-Hosted',
                desc: 'Air-gapped deployment for maximum security.',
                features: ['Complete isolation', 'No external connections', 'On-premise support'],
                best: false
              },
            ].map((mode, i) => (
              <div key={i} className={`enterprise-card p-6 relative ${mode.best ? 'border border-[#0066FF]' : ''}`}>
                {mode.best && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#0066FF] text-white text-[10px] font-bold uppercase tracking-wider">
                    Recommended
                  </div>
                )}
                <mode.icon className="w-8 h-8 text-[#0066FF] mb-4" />
                <h3 className="text-lg font-bold text-[#F9FAFB] mb-2">{mode.title}</h3>
                <p className="text-[#9CA3AF] text-sm mb-5">{mode.desc}</p>
                <ul className="space-y-2">
                  {mode.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                      <Check className="w-3.5 h-3.5 text-[#34C759]" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Architecture */}
      <section id="security" className="py-20 bg-[#121212]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Security Architecture
            </h2>
            <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto">
              {t('security_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t('security_principles').map((item, i) => {
              const icons = [Eye, Server, FileText, Lock, Database, CheckCircle];
              const Icon = icons[i] || Shield;
              return (
              <div key={i} className="enterprise-card p-5">
                <Icon className="w-6 h-6 text-[#0066FF] mb-3" />
                <h3 className="text-base font-bold text-[#F9FAFB] mb-1">{item.title}</h3>
                <p className="text-sm text-[#9CA3AF]">{item.desc}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* For Teams */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('built_for_title')}
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {['ciso', 'identity', 'cloud', 'platform'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === tab 
                    ? 'bg-[#0066FF] text-white' 
                    : 'bg-[#1A1A1A] text-[#9CA3AF] hover:text-[#F9FAFB]'
                }`}
              >
                {t('built_for_tabs')[tab]}
              </button>
            ))}
          </div>

          <div className="enterprise-card p-8">
            {activeTab === 'ciso' && (
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">{t('built_for_ciso_title')}</h3>
                  <p className="text-[#9CA3AF] mb-5 text-sm leading-relaxed">{t('built_for_ciso_desc')}</p>
                  <ul className="space-y-2">
                    {t('built_for_ciso_items').map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                        <Check className="w-4 h-4 text-[#34C759]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#0A0A0A] p-5 border border-[#27272A]/40">
                  <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-3">{t('built_for_ciso_preview')}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-[#121212]">
                      <div className="text-xl font-mono font-bold text-[#34C759]">82</div>
                      <div className="text-[10px] text-[#6B7280] uppercase">{t('built_for_ciso_metric_1')}</div>
                    </div>
                    <div className="p-3 bg-[#121212]">
                      <div className="text-xl font-mono font-bold text-[#FF3B30]">3</div>
                      <div className="text-[10px] text-[#6B7280] uppercase">{t('built_for_ciso_metric_2')}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'identity' && (
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">{t('built_for_identity_title')}</h3>
                  <p className="text-[#9CA3AF] mb-5 text-sm leading-relaxed">{t('built_for_identity_desc')}</p>
                  <ul className="space-y-2">
                    {t('built_for_identity_items').map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                        <Check className="w-4 h-4 text-[#34C759]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#0A0A0A] p-5 border border-[#27272A]/40">
                  <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-3">{t('built_for_identity_preview')}</div>
                  <div className="space-y-2">
                    {['LangChain Agents', 'AWS Service Accounts', 'Okta Service Users'].map((label, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-[#121212]">
                        <span className="text-xs text-[#9CA3AF]">{label}</span>
                        <span className="text-xs font-mono text-[#0066FF]">Discovered</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'cloud' && (
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">{t('built_for_cloud_title')}</h3>
                  <p className="text-[#9CA3AF] mb-5 text-sm leading-relaxed">{t('built_for_cloud_desc')}</p>
                  <ul className="space-y-2">
                    {t('built_for_cloud_items').map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                        <Check className="w-4 h-4 text-[#34C759]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#0A0A0A] p-5 border border-[#27272A]/40">
                  <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-3">{t('built_for_cloud_preview')}</div>
                  <div className="space-y-2">
                    {[{name: 'AWS', count: 142}, {name: 'Azure', count: 67}, {name: 'GCP', count: 38}].map((cloud, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-[#121212]">
                        <span className="text-xs text-[#9CA3AF]">{cloud.name}</span>
                        <span className="text-xs font-mono text-[#F9FAFB]">{cloud.count} identities</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'platform' && (
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">{t('built_for_platform_title')}</h3>
                  <p className="text-[#9CA3AF] mb-5 text-sm leading-relaxed">{t('built_for_platform_desc')}</p>
                  <ul className="space-y-2">
                    {t('built_for_platform_items').map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                        <Check className="w-4 h-4 text-[#34C759]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#0A0A0A] p-5 border border-[#27272A]/40">
                  <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-3">{t('built_for_platform_preview')}</div>
                  <div className="space-y-2">
                    {[{name: 'LangChain', status: 'Active'}, {name: 'CrewAI', status: 'Monitored'}, {name: 'AutoGPT', status: 'Restricted'}].map((fw, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-[#121212]">
                        <span className="text-xs text-[#9CA3AF]">{fw.name}</span>
                        <span className={`text-[10px] font-mono uppercase ${fw.status === 'Active' ? 'text-[#34C759]' : fw.status === 'Monitored' ? 'text-[#FFCC00]' : 'text-[#FF3B30]'}`}>{fw.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#121212] border-t border-[#27272A]/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('cta_title')}
          </h2>
          <p className="text-lg text-[#9CA3AF] mb-8">
            {t('cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link to="/request-pilot">
              <Button className="primary-btn text-base px-8 py-5" data-testid="cta-request-pilot-btn">
                {t('cta_pilot')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" className="secondary-btn text-base px-8 py-5" data-testid="cta-view-demo-btn">
                {t('cta_explore')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[#6B7280]">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#34C759]" />
              <span>{t('hero_trust_3')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#34C759]" />
              <span>{t('hero_trust_2')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#34C759]" />
              <span>{t('hero_trust_1')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-[#0A0A0A] border-t border-[#27272A]/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0066FF]" />
              <span className="text-base font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Agent Control Plane
              </span>
            </div>
            <div className="text-[#6B7280] text-sm">
              &copy; 2026 Agent Control Plane. {t('footer_desc')}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
